import { VirtualList, deviceDetector } from "@ospweb/sdk-ui";
import { useCallback, useMemo } from "react";
import log from "logbeacon";
import { cn } from "utils";
import { SourceReportItem } from "../SourceReport/SourceReportItem";
import Translations from "@translate/components/CoPilot/CopilotAgent/Thinking/Thinking";
import { I18nUtil } from "@utils/i18n";
import CardLoading from "@/src/components/Chat/Cards/CardLoading";
import { AgentReport } from "@/src/features/api/memberApi";
import { EmptyState } from "@/src/components/Empty";
import { useOnboardingData } from "@/src/components/Portfolio/Onboarding/useOnboardingData";
const I18n = I18nUtil.createScoped(Translations);

export const Thinking = ({
  data,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: {
  data: AgentReport[];
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}) => {
  const { apiAgentsData: agentList } = useOnboardingData(false, false);
  const agent = useCallback(
    (agentId: string) => {
      const agent = agentList?.filter((agent) => agent.id === agentId);
      return agent?.[0] || {};
    },
    [agentList],
  );

  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && fetchNextPage) {
      log.debug("[Thinking] onEndReached, fetching next page");
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  /**
   * 渲染加载中
   */
  const renderLoading = useMemo(() => {
    if (!hasNextPage || !isFetchingNextPage) {
      return null;
    }
    return <CardLoading className="min-h-18 w-full" />;
  }, [hasNextPage, isFetchingNextPage]);

  /**
   * 渲染没有更多
   */
  const renderNoMore = useMemo(() => {
    if (hasNextPage) {
      return null;
    }
    return (
      <span className="body_s text-content_quaternary">
        {I18n.t("No more data")}
      </span>
    );
  }, [hasNextPage]);

  if (!isLoading && !data.length) {
    return (
      <div className="flex flex-col w-full h-[calc(100vh-268px)] items-center justify-center">
        <EmptyState
          title={I18n.t("No Smart Signals Yet")}
          description={I18n.t(
            "Check other agents, I’ll alert you when this one finds something worth your attention.",
          )}
          className="w-full max-w-[320px]"
        />
      </div>
    );
  }
  return (
    <div
      className="flex flex-col relative overflow-y-auto no-scrollbar"
      data-tracking-scope="thinking"
    >
      <VirtualList
        className={cn(
          "flex flex-col gap-4 md:h-[calc(100vh-228px)] h-[calc(100vh-268px)]",
        )}
        hasNextPage={hasNextPage}
        onEndReached={handleEndReached}
        onEndReachedThreshold="40"
        renderLoading={renderLoading}
        renderNoMore={renderNoMore}
        loadingContainerClassName="flex items-center justify-center py-4"
        noMoreContainerClassName="flex items-center justify-center py-4"
      >
        <div className="flex flex-col gap-4">
          {data.map((item) => {
            if (item.type === "ALERT") {
              return (
                <SourceReportItem
                  key={item.id}
                  thinking={item.content?.thinking ?? ""}
                  agent={{
                    name: agent(item?.agent_id ?? "")?.name || "",
                    id: item?.agent_id ?? "",
                    avatar: agent(item?.agent_id ?? "")?.avatar || "",
                    label: agent(item?.agent_id ?? "")?.label || "",
                  }}
                  created={item.created || Date.now()}
                  type={item.type ?? ""}
                  enableCollapse={false}
                />
              );
            }
            return null;
          })}
          {deviceDetector.isMobile() && <div className="h-[40px] w-full"></div>}
        </div>
      </VirtualList>
    </div>
  );
};
